import { produce } from 'immer';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { ConversationSessionsTypeUnion, ConversationType } from '@/models/conversations/conversation-session';
import { queryClient } from '@/utils/query-client';
import { parseSessionKey, type SessionKey } from './session';

export const updateSessionUnreadCount = (
  type: ConversationType,
  id: number,
  unreadCountUpdater?: number | ((session: ConversationSessionsTypeUnion) => number | undefined),
) => {
  queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
    produce(state, (draft) => {
      if (!draft) return;

      const session = draft.find((session) => session.SessionType === type && session.ID === id);
      if (session) {
        const newUnreadCount =
          typeof unreadCountUpdater === 'function' ? unreadCountUpdater(session) : unreadCountUpdater;
        if (newUnreadCount !== undefined) {
          session.UnReadCount = newUnreadCount;
          session.IsNewSession = false;

          if (newUnreadCount === 0) {
            session.UnReadMaxID = 0;
            if (session.SessionType === 'session') {
              session.ReadChatID = session.LastMsgID;
            } else if (session.SessionType === 'group') {
              session.ReadGroupChatID = session.LastMsgID;
            }
          }
        }
      }
    }),
  );
};

export const getAllSessions = () =>
  queryClient.getQueryData<ConversationSessionsTypeUnion[]>(allChatSessionQueryOptions.queryKey) ?? [];

export const getSessions = (type: ConversationType) => getAllSessions().filter((s) => s.SessionType === type);

export const getSessionById = (type: ConversationType, id: number) =>
  getAllSessions().find((s) => s.ID === id && s.SessionType === type);

export const getSessionByKey = (key: SessionKey) => {
  const [type, id] = parseSessionKey(key);
  if (!id) return;
  return getSessionById(type, id);
};

export const ensureAllSessions = async () => {
  await queryClient.ensureQueryData(allChatSessionQueryOptions);
};
